import type { DashboardData } from '@/entities/interaction';
import type { GeoSearchParams, Profile, ProfileDetail, SearchFilters } from '@/entities/profile';
import type { User } from '@/entities/user';
import { apiClient } from '@/shared/api/api-client';
import type { ApiResponse, PaginatedResponse } from '@/shared/api/types';

// Helper to map API profile (snake_case) to frontend Profile (camelCase)
export interface ApiProfile {
  node_id: number;
  node_type: number;
  node_state?: number;
  birthday: string;
  last_activity: string;
  profession?: string;
  description?: string;
  city?: string;
  country?: string;
  region?: string;
  zip?: string;
  geo_id?: number;
  username: string;
  avatar_path?: string;
  marital_status?: number;
  administrative_state?: number;
  origin?: number;
  origin_label?: string;
  nationality?: number;
  nationality_label?: string;
  age?: number;
  is_photo_verified?: boolean;
  note?: string;
  is_online?: boolean;
  // Extended profile fields
  size?: number;
  study_level?: number;
  node_business?: number;
  is_study_level_matter?: number;
  children?: number;
  living_place?: number;
  peer_past?: number;
  family_values?: number;
  licence?: number;
  smoke?: number;
  faith?: number;
  minaj?: number;
  veil?: number;
  spoken_languages?: string;
  study_country?: number;
  life_personality?: string;
  shape?: number;
  eye?: number;
  hair?: number;
}

export const mapApiProfileToProfile = (apiProfile: ApiProfile): Profile => ({
  nodeId: apiProfile.node_id,
  nodeType: apiProfile.node_type,
  nodeState: apiProfile.node_state,
  birthday: apiProfile.birthday,
  lastActivity: apiProfile.last_activity,
  profession: apiProfile.profession,
  description: apiProfile.description,
  city: apiProfile.city,
  country: apiProfile.country,
  region: apiProfile.region,
  zip: apiProfile.zip,
  geoId: apiProfile.geo_id || undefined,
  username: apiProfile.username,
  avatarPath: apiProfile.avatar_path,
  maritalStatus: apiProfile.marital_status,
  administrativeState: apiProfile.administrative_state,
  origin: apiProfile.origin,
  originLabel: apiProfile.origin_label || undefined,
  nationality: apiProfile.nationality,
  nationalityLabel: apiProfile.nationality_label || undefined,
  age: apiProfile.age,
  isPhotoVerified: apiProfile.is_photo_verified,
  note: apiProfile.note,
  isOnline: apiProfile.is_online,
  // Extended profile fields
  size: apiProfile.size,
  studyLevel: apiProfile.study_level,
  nodeBusiness: apiProfile.node_business,
  isStudyLevelMatter: apiProfile.is_study_level_matter,
  children: apiProfile.children,
  livingPlace: apiProfile.living_place,
  peerPast: apiProfile.peer_past,
  familyValues: apiProfile.family_values,
  licence: apiProfile.licence,
  smoke: apiProfile.smoke,
  faith: apiProfile.faith,
  minaj: apiProfile.minaj,
  veil: apiProfile.veil,
  spokenLanguages: apiProfile.spoken_languages,
  studyCountry: apiProfile.study_country,
  lifePersonality: apiProfile.life_personality,
  shape: apiProfile.shape,
  eye: apiProfile.eye,
  hair: apiProfile.hair,
});

// Trust status response from API
export interface TrustStatus {
  hasSentRequest: boolean;
  hasReceivedRequest: boolean;
  isTrusted: boolean;
  isRefused: boolean;
  interactionId?: number;
}

// Meeting status response from API
export interface MeetingStatus {
  hasSentRequest: boolean;
  hasReceivedRequest: boolean;
  isMeetingAccepted: boolean;
  isRefused: boolean;
  interactionId?: number;
}

// Full status combining trust and meeting
export interface FullStatus {
  trust: TrustStatus;
  meeting: MeetingStatus;
}

// Private profile field
export interface PrivateProfileField {
  name: string;
  value: string;
}

// Interaction profile types
export interface InteractionProfile {
  interactionId: number;
  nodeId: number;
  username: string;
  age: number;
  city?: string;
  region?: string;
  profession?: string;
  avatarPath?: string;
  maritalStatus?: number;
  administrativeState?: number;
  origin?: number;
  nationality?: number;
  isPhotoVerified: boolean;
  note?: string;
  interactionTypeId: number;
  createdAt: string;
  updatedAt: string;
}

export interface InteractionProfilesResponse {
  profiles: InteractionProfile[];
  total: number;
  page: number;
  count: number;
}

// Profile update request interface (maps camelCase to snake_case for API)
interface UpdateProfileData {
  description?: string;
  zipCode?: string;
  /** geolocalisation id picked among the zip's towns; wins over zipCode */
  geoId?: number;
  profession?: string;
  avatarId?: number;
  studyLevel?: number;
  nodeBusiness?: number;
  isStudyLevelMatter?: number;
  maritalStatus?: number;
  children?: number;
  livingPlace?: number;
  licence?: number;
  smoke?: number;
  faith?: number;
  minaj?: number;
  veil?: number;
  familyValues?: number;
  peerPast?: number;
  administrativeState?: number;
  nationality?: number;
  origin?: number;
  studyCountry?: number;
  spokenLanguages?: string;
  lifePersonality?: string;
  shape?: number;
  size?: number;
  eye?: number;
  hair?: number;
}

// A block hides both members from each other until the blocker lifts it.
interface ApiBlockedMember {
  node_id: number;
  username: string;
  avatar_path: string;
  blocked_at: string;
}

export interface BlockedMember {
  nodeId: number;
  username: string;
  avatarPath: string;
  blockedAt: string;
}

// User API
export const userApi = {
  getCurrentUser: async (): Promise<User> => {
    const response = await apiClient.get<ApiResponse<User>>('/v1/user/info');
    return response.data.data;
  },

  getMyProfile: async (nodeId: number): Promise<Profile> => {
    const response = await apiClient.get<ApiResponse<{ profile: ApiProfile }>>(
      `/v1/profile/${nodeId}`,
    );
    return mapApiProfileToProfile(response.data.data.profile);
  },

  updateProfile: async (data: UpdateProfileData): Promise<Profile> => {
    const response = await apiClient.put<ApiResponse<ApiProfile>>('/v1/user/profile', data);
    return mapApiProfileToProfile(response.data.data);
  },

  /**
   * Private questions (legacy signup step 8), keyed by legacy field name.
   * Values: choice index, list of indexes (multi-choice) or free text.
   */
  updatePrivateProfile: async (
    fields: Record<string, string | number | number[] | string[]>,
  ): Promise<string[]> => {
    const response = await apiClient.put<ApiResponse<{ updated: string[] }>>(
      '/v1/user/profile/private',
      { fields },
    );
    return response.data.data.updated || [];
  },

  uploadPhoto: async (photo: Blob): Promise<{ filename: string; path: string }> => {
    const formData = new FormData();
    formData.append('photo', photo, 'photo.png');
    const response = await apiClient.post<ApiResponse<{ filename: string; path: string }>>(
      '/v1/user/photo',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );
    return response.data.data;
  },

  deactivateAccount: async (): Promise<void> => {
    await apiClient.post('/v1/user/deactivate');
  },

  deleteAccount: async (): Promise<void> => {
    await apiClient.delete('/v1/user/account');
  },

  // Members blocked by the current user (settings, to unblock them)
  getBlockedMembers: async (): Promise<BlockedMember[]> => {
    const response =
      await apiClient.get<ApiResponse<{ members: ApiBlockedMember[] }>>('/v1/user/blocked');
    return (response.data.data.members ?? []).map((m) => ({
      nodeId: m.node_id,
      username: m.username,
      avatarPath: m.avatar_path,
      blockedAt: m.blocked_at,
    }));
  },
};

// Profile API
export const profileApi = {
  getProfiles: async (
    filters?: SearchFilters,
    page = 1,
    count = 10,
  ): Promise<PaginatedResponse<Profile>> => {
    const response = await apiClient.get<ApiResponse<{ profiles: ApiProfile[]; total: number }>>(
      '/v1/profile',
      {
        params: { ...filters, page, count },
      },
    );
    // Map API response (profiles with snake_case) to frontend format (items with camelCase)
    return {
      items: (response.data.data.profiles || []).map(mapApiProfileToProfile),
      total: response.data.data.total,
      page,
      count,
    };
  },

  getGeoProfiles: async (params?: GeoSearchParams): Promise<PaginatedResponse<Profile>> => {
    const response = await apiClient.get<ApiResponse<{ profiles: ApiProfile[]; total: number }>>(
      '/v1/profile/geosearch',
      { params },
    );
    // Map API response (profiles with snake_case) to frontend format (items with camelCase)
    return {
      items: (response.data.data.profiles || []).map(mapApiProfileToProfile),
      total: response.data.data.total,
      page: params?.page || 1,
      count: params?.count || 10,
    };
  },

  getProfile: async (nodeId: number): Promise<ProfileDetail> => {
    const response = await apiClient.get<ApiResponse<{ profile: ApiProfile }>>(
      `/v1/profile/${nodeId}`,
    );
    const apiProfile = response.data.data.profile;
    return {
      ...mapApiProfileToProfile(apiProfile),
      // ProfileDetail-only fields (description comes from the mapping above:
      // overriding it here hid every member's bio)
      hasTrustRequested: false,
    } as ProfileDetail;
  },

  requestContact: async (nodeId: number): Promise<void> => {
    await apiClient.post(`/v1/profile/${nodeId}`);
  },

  // Trust request methods
  getTrustStatus: async (nodeId: number): Promise<TrustStatus> => {
    const response = await apiClient.get<ApiResponse<TrustStatus>>(`/v1/profile/${nodeId}/trust`);
    return response.data.data;
  },

  sendTrustRequest: async (nodeId: number): Promise<void> => {
    await apiClient.post(`/v1/profile/${nodeId}/trust`);
  },

  acceptTrustRequest: async (nodeId: number): Promise<void> => {
    await apiClient.post(`/v1/profile/${nodeId}/trust/accept`);
  },

  refuseTrustRequest: async (nodeId: number): Promise<void> => {
    await apiClient.post(`/v1/profile/${nodeId}/trust/refuse`);
  },

  getReceivedTrustRequests: async (page = 1, count = 20): Promise<InteractionProfilesResponse> => {
    const response = await apiClient.get<
      ApiResponse<{ requests: InteractionProfile[]; total: number; page: number; count: number }>
    >('/v1/profile/trust/requests', { params: { page, count } });
    return {
      profiles: response.data.data.requests || [],
      total: response.data.data.total,
      page: response.data.data.page,
      count: response.data.data.count,
    };
  },

  // Full status (trust + meeting)
  getFullStatus: async (nodeId: number): Promise<FullStatus> => {
    const response = await apiClient.get<ApiResponse<FullStatus>>(`/v1/profile/${nodeId}/status`);
    return response.data.data;
  },

  // Private profile
  getPrivateProfile: async (nodeId: number): Promise<PrivateProfileField[]> => {
    const response = await apiClient.get<ApiResponse<{ privateFields: PrivateProfileField[] }>>(
      `/v1/profile/${nodeId}/private`,
    );
    return response.data.data.privateFields || [];
  },

  // Meeting request methods
  sendMeetingRequest: async (nodeId: number): Promise<void> => {
    await apiClient.post(`/v1/profile/${nodeId}/meeting`);
  },

  acceptMeetingRequest: async (nodeId: number): Promise<void> => {
    await apiClient.post(`/v1/profile/${nodeId}/meeting/accept`);
  },

  refuseMeetingRequest: async (nodeId: number): Promise<void> => {
    await apiClient.post(`/v1/profile/${nodeId}/meeting/refuse`);
  },

  // Block a member: both members are then hidden from each other
  blockMember: async (nodeId: number): Promise<void> => {
    await apiClient.post(`/v1/profile/${nodeId}/block`);
  },

  // Lift the block set on a member
  unblockMember: async (nodeId: number): Promise<void> => {
    await apiClient.delete(`/v1/profile/${nodeId}/block`);
  },

  // Interaction profiles (used by interactions page)
  getInteractionProfiles: async (
    type: number,
    direction: 'sent' | 'received' | 'bidirectional' = 'bidirectional',
    page = 1,
    count = 20,
  ): Promise<InteractionProfilesResponse> => {
    const response = await apiClient.get<ApiResponse<InteractionProfilesResponse>>(
      '/v1/interactions/profiles',
      {
        params: { type, direction, page, count },
      },
    );
    return response.data.data;
  },
};

// Dashboard API
export const dashboardApi = {
  getDashboard: async (): Promise<DashboardData> => {
    const response = await apiClient.get<ApiResponse<DashboardData>>('/v1/dashboard');
    return response.data.data;
  },
};

// Matches API
export const matchesApi = {
  getMatches: async (): Promise<Profile[]> => {
    const response = await apiClient.get<ApiResponse<Profile[]>>('/v1/matches');
    return response.data.data;
  },
};
